import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calculator,
  ScrollText,
  Receipt,
  ArrowLeftRight,
  Check,
  ArrowUpRight,
  Clock,
  ShieldCheck,
  FileSpreadsheet,
  TrendingUp,
  AlertTriangle,
  Briefcase,
  Target,
  Layers,
  Scale,
  Wrench,
  Calendar,
} from "lucide-react";
import HeaderV2 from "./HeaderV2";
import FooterV2 from "./FooterV2";
import V2PageHero from "./sections/V2PageHero";
import FAQV2 from "./sections/FAQV2";
import CTAV2 from "./sections/CTAV2";
import InlineCTA from "./sections/InlineCTA";
import useSmoothScroll from "../../hooks/useSmoothScroll";
import { useConsultation } from "../../context/ConsultationContext";

const HEADER_IMG =
  "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=2400&q=80";
const HEADER_IMG_DARK =
  "https://images.unsplash.com/photo-1554224154-22dec7ec8818?auto=format&fit=crop&w=2400&q=80";

const SERVICES = {
  accounting: {
    icon: Calculator,
    eyebrow: "Service · Accounting & Bookkeeping",
    title: "Audit-grade accounting,",
    highlight: "without the in-house overhead.",
    copy: "Cloud-native bookkeeping, monthly close, management reporting and IFRS-aligned financial statements — built so your numbers are always investor-ready and audit-ready.",
    intro:
      "Most UAE SMEs run their books in spreadsheets until something breaks — a bank request, a VAT audit, a fundraise. StartINC delivers a finance function that operates like a 50-person company, embedded into a single advisor relationship at a fraction of the cost.",
    image: HEADER_IMG,
    bullets: [
      "Daily bookkeeping in Xero, Zoho Books or QuickBooks",
      "Monthly close with management P&L, balance sheet, cash-flow",
      "IFRS-aligned annual financial statements",
      "Receivables, payables and bank reconciliation",
      "Multi-entity, multi-currency consolidations",
      "Audit liaison and year-end audit-ready packs",
    ],
    sections: [
      {
        title: "What you get every month",
        copy: "A finance function that runs on autopilot — built so your team can focus on growth, not reconciliations.",
        items: [
          { icon: FileSpreadsheet, t: "Cloud bookkeeping", d: "Live ledgers in Xero / Zoho / QuickBooks." },
          { icon: TrendingUp, t: "Management reporting", d: "P&L, balance sheet, cash-flow by the 15th." },
          { icon: ShieldCheck, t: "Compliance ready", d: "VAT, CT, ESR & UBO files always current." },
          { icon: Clock, t: "On-demand support", d: "Senior accountant on Slack / WhatsApp." },
        ],
      },
      {
        title: "What's typically broken — and how we fix it",
        copy: "Symptoms our clients had before us, and how a clean accounting backbone solves them in 30 days.",
        bg: "dark",
        items: [
          { icon: AlertTriangle, t: "Books 6+ months behind", d: "We catch up your historical ledgers in 2–3 weeks, with reconciled bank balances." },
          { icon: AlertTriangle, t: "VAT returns guessed", d: "Output and input tax reconciled to invoices and ledger — no more last-minute scramble." },
          { icon: AlertTriangle, t: "No real P&L", d: "Department- and project-level profitability you can actually trust each month." },
          { icon: AlertTriangle, t: "Audit panic in March", d: "Year-end packs prepared inline with IFRS so your auditor signs off in days, not months." },
        ],
      },
      {
        title: "Software & integrations we operate",
        copy: "We work with the tools you already trust — and recommend the cleanest stack when you don't have one yet.",
        items: [
          { icon: Wrench, t: "Xero", d: "Most popular for SMEs and consultants." },
          { icon: Wrench, t: "Zoho Books", d: "Best in-class for UAE VAT compliance." },
          { icon: Wrench, t: "QuickBooks", d: "Preferred by US-headquartered teams." },
          { icon: Wrench, t: "Excel + GL", d: "Custom group consolidations for holdings." },
        ],
      },
    ],
    pricing: { time: "Live in 1 week", from: "from AED 500 / month*" },
    bestFor: ["Founders & SMEs", "Multi-entity holdings", "Investor-backed startups", "E-commerce & SaaS"],
    relatedSlug: "vat",
    faqs: [
      {
        q: "How quickly can you take over our books?",
        a: "Once you give us access to your bank statements and existing accounting tool (Xero, Zoho, QuickBooks or even Excel), we typically complete a full handover in 5–10 working days. If your books are 3–6 months behind, our catch-up engagement reconciles everything inside 2–3 weeks before we move you onto a normal monthly cycle.",
      },
      {
        q: "Which accounting software do you support?",
        a: "We are certified partners on Xero, Zoho Books and QuickBooks Online. We can migrate from any of these (or from spreadsheets / Tally) to your preferred system. We typically recommend Zoho Books for UAE-only SMEs because of its native VAT and FTA invoice handling, and Xero for multi-currency / international groups.",
      },
      {
        q: "Will I have a dedicated accountant?",
        a: "Yes — every engagement is led by a senior UAE-qualified accountant who is your single point of contact. They run your monthly close, attend your finance reviews, and handle ad-hoc queries on Slack / WhatsApp. There are no call-centre handoffs.",
      },
      {
        q: "Do you handle the year-end audit too?",
        a: "We don't perform the statutory audit ourselves (UAE rules require an independent firm), but we prepare the entire audit-ready pack — IFRS-aligned financial statements, schedules, supporting documents and management letters. We then liaise directly with your auditor to close it efficiently, typically saving 40–60% of audit fees and time.",
      },
      {
        q: "Can you consolidate multiple entities?",
        a: "Yes. We routinely run group accounts across UAE freezone, mainland and offshore entities, plus international subsidiaries. We handle multi-currency translation, intercompany eliminations and produce both standalone and consolidated reports for your board.",
      },
      {
        q: "What does AED 500 / month actually cover?",
        a: "Our entry tier covers up to ~50 transactions / month, monthly P&L and balance sheet, bank reconciliation and an annual financial statement. Higher volumes, payroll, multi-entity consolidations or weekly reporting are priced incrementally — always in writing, before any work starts.",
      },
    ],
  },

  "corporate-tax": {
    icon: ScrollText,
    eyebrow: "Service · UAE Corporate Tax",
    title: "9% UAE Corporate Tax —",
    highlight: "structured & filed correctly.",
    copy: "From CT registration with the Federal Tax Authority to qualifying-income analysis, transfer pricing alignment and annual return filing — we manage the full corporate-tax cycle.",
    intro:
      "UAE Corporate Tax came into force on 1 June 2023 at a 9% headline rate. The real complexity isn't the calculation — it's identifying which of your revenue streams qualify for the 0% Free Zone regime, structuring related-party flows, and filing the return on time. StartINC turns the analysis into a checklist, not a guess.",
    image: HEADER_IMG_DARK,
    bullets: [
      "Federal Tax Authority (FTA) Corporate Tax registration",
      "Qualifying Freezone Person (QFZP) eligibility analysis",
      "Tax-period accounting policies & elections",
      "Annual Corporate Tax return preparation & filing",
      "Provision and effective tax rate (ETR) modelling",
      "Group structuring & tax loss management",
    ],
    sections: [
      {
        title: "Where founders need help",
        copy: "Most UAE businesses underestimate the analysis required to maintain a 0% rate on qualifying free-zone income.",
        items: [
          { icon: ScrollText, t: "FTA registration", d: "Within 90 days of incorporation / fiscal year end." },
          { icon: ShieldCheck, t: "QFZP analysis", d: "Qualifying activity, qualifying income & substance testing." },
          { icon: TrendingUp, t: "ETR modelling", d: "Forecast tax cash-flow before year-end." },
          { icon: Clock, t: "Return filing", d: "Annual return within 9 months of fiscal year end." },
        ],
      },
      {
        title: "Key dates you can't miss",
        copy: "UAE Corporate Tax compliance is calendar-driven. Late registration alone is now AED 10,000. We track every deadline for you.",
        bg: "dark",
        items: [
          { icon: Calendar, t: "Registration deadline", d: "Within 3 months of incorporation or as per FTA grid for existing entities." },
          { icon: Calendar, t: "First tax period", d: "12 months from your accounting reference date — usually 1 Jan to 31 Dec." },
          { icon: Calendar, t: "Return filing", d: "Within 9 months of fiscal year-end (e.g. 30 Sept for a Dec year)." },
          { icon: Calendar, t: "Tax payment", d: "Same 9-month window — paid via the FTA EmaraTax portal." },
        ],
      },
      {
        title: "QFZP — the 0% regime, explained",
        copy: "Qualifying Freezone Person status keeps your free-zone income at 0%. Five tests must be met simultaneously, and we help you pass each one.",
        items: [
          { icon: Target, t: "Adequate substance", d: "Real people, real office, real economic activity in the free zone." },
          { icon: Target, t: "Qualifying income", d: "Trade with non-UAE persons / other QFZPs / specific qualifying activities." },
          { icon: Target, t: "De-minimis test", d: "Non-qualifying revenue ≤ 5% of total or AED 5M, whichever is lower." },
          { icon: Target, t: "Arm's-length pricing", d: "TP rules apply — see our Transfer Pricing service." },
        ],
      },
    ],
    pricing: { time: "Registration in 7–10 days", from: "from AED 500 one-time*" },
    bestFor: ["Mainland LLCs", "Freezone entities (QFZP)", "Holdings & groups", "Cross-border traders"],
    relatedSlug: "transfer-pricing",
    faqs: [
      {
        q: "Do I need to register for UAE Corporate Tax even if my profit is below AED 375,000?",
        a: "Yes. Every taxable person — mainland, freezone or offshore — must register with the FTA, regardless of profit level. The 0% rate up to AED 375,000 (and the Small Business Relief up to AED 3M revenue) is a tax outcome, not an exemption from registration. Late registration is an AED 10,000 administrative penalty.",
      },
      {
        q: "Can my Free Zone company stay at 0% Corporate Tax?",
        a: "Yes — provided you meet all five Qualifying Freezone Person (QFZP) conditions: adequate substance, qualifying income, de-minimis on non-qualifying revenue, audited financials, and arm's-length pricing on related-party transactions. We perform a written QFZP analysis at the start of each tax period and flag any threats to your 0% status.",
      },
      {
        q: "What is Small Business Relief and do I qualify?",
        a: "Small Business Relief lets a resident UAE business with revenue ≤ AED 3 million in the relevant and prior tax periods elect to be treated as having NO taxable income — effectively a 0% rate. The relief runs to 31 December 2026. We assess eligibility every year because revenue creep can disqualify you mid-period.",
      },
      {
        q: "How long does it take to register for Corporate Tax with the FTA?",
        a: "From document submission, the EmaraTax portal usually issues a Tax Registration Number within 7–10 working days. We prepare the full document set (trade license, MoA, ID copies, banking details) and submit on your behalf so you don't get bounced for missing documents.",
      },
      {
        q: "Do I still need to file a Corporate Tax return if my taxable income is zero?",
        a: "Yes. Every registered taxable person must file an annual Corporate Tax return within 9 months of the fiscal year-end — even if the result is zero or a loss. Failure to file is AED 500 / month escalating to AED 1,000 / month after the first 12 months.",
      },
      {
        q: "How does StartINC structure the engagement?",
        a: "Three phases: (1) one-time CT registration and QFZP/SBR analysis from AED 500, (2) annual CT return preparation in the 8th month after year-end, (3) optional quarterly tax-provision modelling for groups that want predictable cash-flow forecasts. All scoped in writing before we start.",
      },
    ],
  },

  vat: {
    icon: Receipt,
    eyebrow: "Service · VAT",
    title: "5% UAE VAT —",
    highlight: "registered, filed, refunded.",
    copy: "VAT registration, return filing, refund claims and FTA audit support. Built for businesses that want to stop chasing receipts and start trusting their compliance.",
    intro:
      "UAE VAT looks simple at 5% — until you're chasing missing tax invoices, debating exempt vs zero-rated, or stuck in an FTA audit. StartINC operates VAT as an end-to-end workflow: invoicing standards, period reconciliations, refund claims, and audit defence — all in one engagement.",
    image: HEADER_IMG,
    bullets: [
      "VAT registration with the FTA (TRN issuance)",
      "Quarterly or monthly VAT return preparation",
      "Input-tax recovery and refund claim handling",
      "Tax invoice formatting and POS configuration",
      "VAT health checks and FTA audit support",
      "Voluntary disclosures and penalty mitigation",
    ],
    sections: [
      {
        title: "End-to-end VAT cover",
        copy: "We don't just compute the tax — we engineer the workflow so your VAT position is always defensible.",
        items: [
          { icon: Receipt, t: "Registration & TRN", d: "FTA portal, supporting docs, follow-ups." },
          { icon: FileSpreadsheet, t: "Return filing", d: "Quarterly VAT 201 prepared & filed." },
          { icon: TrendingUp, t: "Refund claims", d: "Excess input-tax recovery with FTA." },
          { icon: ShieldCheck, t: "Audit support", d: "Reconciliations, queries, voluntary disclosure." },
        ],
      },
      {
        title: "Mandatory vs Voluntary registration",
        copy: "Two thresholds, one decision. We assess your trailing-12-month and forward-30-day revenue to recommend the right path.",
        bg: "dark",
        items: [
          { icon: Layers, t: "Mandatory: AED 375,000", d: "Required if your taxable supplies exceed this in any rolling 12 months." },
          { icon: Layers, t: "Voluntary: AED 187,500", d: "Optional for businesses above this — useful to recover input VAT early." },
          { icon: Layers, t: "Exception thresholds", d: "Specific exemptions for designated zones, healthcare, education." },
          { icon: Layers, t: "De-registration", d: "We handle it cleanly when revenue drops below thresholds." },
        ],
      },
      {
        title: "Common VAT mistakes we fix",
        copy: "Recurring issues we see when we take over a client's VAT — and the fix in each case.",
        items: [
          { icon: AlertTriangle, t: "Wrong tax codes", d: "Standard / zero-rated / exempt confusion on services & exports." },
          { icon: AlertTriangle, t: "Missing tax invoices", d: "Compliant TRN, breakdown and Arabic mentions absent." },
          { icon: AlertTriangle, t: "Late returns / payments", d: "Penalties accruing — we file voluntary disclosures to mitigate." },
          { icon: AlertTriangle, t: "Unclaimed input VAT", d: "Recoverable VAT sitting in P&L — we file refund claims." },
        ],
      },
    ],
    pricing: { time: "TRN in 5–10 working days", from: "from AED 500 / quarter*" },
    bestFor: ["Trading & e-commerce", "F&B and retail", "Service exporters", "Real estate & construction"],
    relatedSlug: "accounting",
    faqs: [
      {
        q: "When must I register for VAT in the UAE?",
        a: "VAT registration is mandatory once your taxable supplies (excluding exempt and out-of-scope items) exceed AED 375,000 in any rolling 12-month window — or when you reasonably expect to exceed that figure in the next 30 days. Voluntary registration is permitted from AED 187,500.",
      },
      {
        q: "How long does it take to obtain a TRN?",
        a: "Typically 5–10 working days from a complete EmaraTax submission. We prepare all supporting documents (trade license, MoA, bank confirmation, sample invoices, customs registration if applicable) so the application doesn't get sent back for clarification.",
      },
      {
        q: "What's the difference between zero-rated, exempt and out-of-scope?",
        a: "Zero-rated supplies (e.g. exports, certain healthcare/education) are taxable at 0% — you can recover input VAT. Exempt supplies (e.g. residential rent, local passenger transport) carry no output VAT but you cannot recover related input VAT. Out-of-scope items fall outside the VAT system entirely. We map every revenue stream you have so this isn't guess-work.",
      },
      {
        q: "Can I claim a VAT refund?",
        a: "Yes. If your input VAT in a tax period exceeds your output VAT, the excess can be carried forward or claimed as a refund via the EmaraTax portal. We handle the full refund file — supporting documents, sample invoices and FTA queries — and have recovered AED 1M+ for individual clients.",
      },
      {
        q: "What happens if I miss a VAT return or payment?",
        a: "Late filing is AED 1,000 (first offence) and AED 2,000 (subsequent). Late payment is 2% immediately, plus 4% on the 7th day, then 1% per day up to 300%. If you've fallen behind, a Voluntary Disclosure can substantially reduce penalties — we file these regularly.",
      },
      {
        q: "What does AED 500 / quarter cover?",
        a: "It covers preparation and filing of a single VAT return for businesses with up to ~75 invoices per quarter. We reconcile your input/output tax to the GL, file the VAT 201 on EmaraTax, and store the full audit trail. Higher volumes and refund claims are priced incrementally.",
      },
    ],
  },

  "transfer-pricing": {
    icon: ArrowLeftRight,
    eyebrow: "Service · Transfer Pricing",
    title: "Transfer Pricing,",
    highlight: "built to OECD standards.",
    copy: "UAE Corporate Tax requires arm's-length pricing and OECD-aligned documentation between related parties. We design and document your TP policy end-to-end.",
    intro:
      "Under UAE Corporate Tax, every related-party and connected-person transaction must be priced at arm's length, supported by the OECD's three-tier documentation: a Master File, a Local File and (for the largest groups) Country-by-Country reporting. The penalty for missing documentation is up to AED 1M. StartINC builds a TP file that withstands scrutiny — and that scales with your group.",
    image: HEADER_IMG_DARK,
    bullets: [
      "Functional, asset and risk (FAR) analysis",
      "Benchmark studies using S&P Capital IQ / TP Catalyst",
      "OECD-aligned Master File and Local File",
      "Country-by-Country reporting (CbCR) where applicable",
      "Inter-company agreements & invoicing templates",
      "Disclosure form preparation for the FTA",
    ],
    sections: [
      {
        title: "A defensible TP file",
        copy: "We build a transfer-pricing position that withstands FTA scrutiny — and that scales as your group grows.",
        items: [
          { icon: ArrowLeftRight, t: "FAR analysis", d: "Function, asset & risk profiling per entity." },
          { icon: TrendingUp, t: "Benchmarking", d: "Comparables and arm's-length range." },
          { icon: FileSpreadsheet, t: "Master & Local File", d: "OECD-compliant documentation." },
          { icon: ShieldCheck, t: "Disclosure forms", d: "FTA-ready supporting schedules." },
        ],
      },
      {
        title: "When TP documentation kicks in",
        copy: "Three thresholds determine what your group must produce. We map them at the start of every engagement.",
        bg: "dark",
        items: [
          { icon: Layers, t: "TP Disclosure Form", d: "Required with every CT return where related-party value exceeds AED 40M." },
          { icon: Layers, t: "Master + Local File", d: "Required when revenue ≥ AED 200M or part of an MNE group with revenue ≥ AED 3.15B." },
          { icon: Layers, t: "Country-by-Country (CbCR)", d: "Required for MNE groups with consolidated revenue ≥ AED 3.15B." },
          { icon: Layers, t: "All others", d: "Still need a defensible position — we provide a 'TP-Lite' file proportional to risk." },
        ],
      },
      {
        title: "OECD methods we apply",
        copy: "We pick the method that best fits your transaction — and document why the others were rejected.",
        items: [
          { icon: Briefcase, t: "CUP (Comparable Uncontrolled Price)", d: "Best for commodities & licensable IP." },
          { icon: Briefcase, t: "Resale Price / Cost Plus", d: "For routine distribution or manufacturing." },
          { icon: Briefcase, t: "TNMM", d: "Net-margin benchmarking — most common for services." },
          { icon: Briefcase, t: "Profit Split", d: "Used when both parties contribute unique IP." },
        ],
      },
      {
        title: "What you receive",
        copy: "Tangible deliverables, not a slide deck. Every artefact is the kind we'd hand to an FTA auditor on day one.",
        bg: "dark",
        items: [
          { icon: Scale, t: "FAR + risk matrix", d: "Documented per entity and per controlled transaction." },
          { icon: Scale, t: "Benchmark study", d: "S&P Capital IQ / TP Catalyst search with selection rationale." },
          { icon: Scale, t: "Master & Local File", d: "OECD-aligned, dated, signed and version-controlled." },
          { icon: Scale, t: "Inter-company agreements", d: "Drafted or reviewed to align with the documented policy." },
        ],
      },
    ],
    pricing: { time: "Documentation in 3–6 weeks", from: "from AED 5,000 one-time*" },
    bestFor: ["UAE holdings & subsidiaries", "Cross-border services", "IP licensing structures", "Groups > AED 200M revenue"],
    relatedSlug: "corporate-tax",
    faqs: [
      {
        q: "Does my UAE business need transfer pricing documentation?",
        a: "If you have any related-party or connected-person transactions — intercompany loans, royalties, management fees, shared services, sales, etc. — you need to apply arm's-length pricing under UAE Corporate Tax law. Whether you also need formal Master/Local File documentation depends on revenue thresholds (AED 200M / AED 3.15B). Below those thresholds, we still recommend a 'TP-Lite' file because the FTA can request justification at any time.",
      },
      {
        q: "What are the UAE TP documentation thresholds?",
        a: "Master File and Local File are required when (a) the taxpayer's revenue is ≥ AED 200M, or (b) the taxpayer is a constituent of an MNE Group with consolidated revenue ≥ AED 3.15B. CbCR is required for MNE Groups with consolidated revenue ≥ AED 3.15B. The Disclosure Form (filed with the CT return) is required when total related-party transactions exceed AED 40M, or any single connected-person transaction exceeds AED 500K.",
      },
      {
        q: "Which OECD method is right for my business?",
        a: "There is no 'default' — you select the most appropriate method based on the nature of the transaction, availability of comparables, and reliability. We routinely apply CUP for commodities and IP licensing, Resale Price / Cost Plus for distribution and manufacturing, TNMM for service charges, and Profit Split for transactions where both parties contribute unique value. Our written TP policy explains why each method was selected.",
      },
      {
        q: "How long does a transfer pricing engagement take?",
        a: "A standard Master File + Local File for a UAE entity with 5–10 controlled transactions takes 3–6 weeks: 1 week of fact-finding (FAR interviews, document collection), 1–2 weeks for benchmarking, 1–2 weeks for drafting, plus a review cycle. CbCR or multi-jurisdictional engagements can extend to 8–12 weeks.",
      },
      {
        q: "What benchmarking databases do you use?",
        a: "We use S&P Capital IQ, RoyaltyRange and Bureau van Dijk's Orbis / TP Catalyst — the same datasets the Big-4 use. Each comparable is screened on financial criteria (turnover, independence, time period) and qualitative criteria (industry, function, geography) and the inter-quartile range is documented in the Local File.",
      },
      {
        q: "Why is your fee from AED 5,000 when others quote 10x more?",
        a: "We've productised the TP delivery — clear playbooks, templated FAR matrices, in-house benchmarking access — so the analysis time is a fraction of what a Big-4 quote assumes. Complex multi-jurisdictional groups still cost more, but a clean single-entity Master + Local File starts at AED 5,000 and is fully OECD- and FTA-compliant.",
      },
    ],
  },
};

export default function ServicePageV2() {
  useSmoothScroll(true);
  const { slug } = useParams();
  const { open } = useConsultation();
  const data = SERVICES[slug];

  if (!data) return <Navigate to="/" replace />;

  const Icon = data.icon;
  const related = SERVICES[data.relatedSlug];
  const labelOnly = data.eyebrow.split("· ").pop();

  return (
    <div data-testid={`v2-service-${slug}-page`} className="bg-white">
      <HeaderV2 />
      <main>
        <V2PageHero
          eyebrow={data.eyebrow}
          title={data.title}
          highlight={data.highlight}
          copy={data.copy}
          image={data.image}
        />

        {/* Intro + bullets */}
        <section className="bg-white py-10 md:py-14">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <p className="overline text-[#3366FF]">What's included</p>
              </div>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
                A complete <span className="font-black text-[#002366]">{labelOnly}</span> engagement — under one roof.
              </h2>
              {data.intro && (
                <p className="mt-5 text-slate-600 leading-relaxed">{data.intro}</p>
              )}
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex gap-3 text-slate-700 text-sm md:text-base"
                  >
                    <Check className="w-5 h-5 shrink-0 text-[#3366FF] mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-5">
              <div className="rounded-sm border border-slate-200 p-6 md:p-7 bg-white shadow-sm lg:sticky lg:top-28">
                <p className="overline text-[#3366FF]">Best for</p>
                <ul className="mt-4 space-y-2.5">
                  {data.bestFor.map((b) => (
                    <li key={b} className="text-slate-800 flex items-center gap-3 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3366FF]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-5 rounded-sm bg-[#002366] text-white">
                  <div className="text-xs text-slate-300 uppercase tracking-wider">
                    Turnaround
                  </div>
                  <div className="mt-1 font-heading font-black text-xl">
                    {data.pricing.time}
                  </div>
                  <div className="mt-3 text-xs text-slate-300 uppercase tracking-wider">
                    Pricing
                  </div>
                  <div className="mt-1 font-heading font-black text-xl text-[#6B8AFF]">
                    {data.pricing.from}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">
                    *Indicative. Final quote depends on scope and entity volume.
                  </p>
                </div>
                <button
                  onClick={() => open("other")}
                  className="btn-gold mt-5 w-full text-sm"
                >
                  Request a quote <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </aside>
          </div>
        </section>

        <InlineCTA
          eyebrow="Ready to outsource?"
          title={`Get a tailored ${labelOnly} engagement scoped in 24 hours.`}
          buttonLabel="Talk to an advisor"
        />

        {/* Sub-sections */}
        {data.sections.map((sec, si) => {
          const dark = sec.bg === "dark";
          return (
            <section
              key={sec.title}
              className={
                dark
                  ? "bg-[#001A4D] text-white py-10 md:py-14"
                  : si % 2 === 0
                  ? "bg-[#F8FAFC] py-10 md:py-14"
                  : "bg-white py-10 md:py-14"
              }
            >
              <div className="container-x">
                <div className="max-w-2xl mb-7">
                  <p
                    className={`overline ${
                      dark ? "text-[#6B8AFF]" : "text-[#3366FF]"
                    }`}
                  >
                    {sec.title}
                  </p>
                  <div className="gold-rule mt-5 mb-6" />
                  <p
                    className={`leading-relaxed ${
                      dark ? "text-slate-300/85" : "text-slate-600"
                    }`}
                  >
                    {sec.copy}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {sec.items.map(({ icon: I, t, d }, i) => (
                    <motion.div
                      key={t}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                      className={`p-6 rounded-sm transition-all ${
                        dark
                          ? "bg-white/5 border border-white/10 hover:border-[#6B8AFF]/40"
                          : "bg-white border border-slate-200 hover:shadow-md hover:border-[#3366FF]/40"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-sm inline-flex items-center justify-center ${
                          dark
                            ? "bg-white/10 text-[#6B8AFF]"
                            : "bg-[#EEF2FF] text-[#3366FF]"
                        }`}
                      >
                        <I className="w-4 h-4" />
                      </div>
                      <h3
                        className={`mt-4 font-heading font-semibold ${
                          dark ? "text-white" : "text-[#0F172A]"
                        }`}
                      >
                        {t}
                      </h3>
                      <p
                        className={`mt-1 text-sm leading-relaxed ${
                          dark ? "text-slate-300/80" : "text-slate-600"
                        }`}
                      >
                        {d}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* Related service */}
        {related && (
          <section className="bg-[#001A4D] text-white py-10 md:py-14">
            <div className="container-x flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="overline text-[#6B8AFF]">Related service</p>
                <h3 className="mt-3 font-heading font-light text-xl md:text-2xl tracking-tight max-w-xl">
                  Pair this with{" "}
                  <span className="font-black text-[#6B8AFF]">
                    {related.eyebrow.split("· ").pop()}
                  </span>{" "}
                  for an end-to-end finance function.
                </h3>
              </div>
              <Link
                to={`/services/${data.relatedSlug}`}
                className="btn-outline-gold text-sm shrink-0"
              >
                Explore {related.eyebrow.split("· ").pop()}{" "}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        )}

        <FAQV2
          eyebrow={`${labelOnly} · FAQ`}
          title={
            <>
              Common questions on{" "}
              <span className="font-black text-[#002366]">{labelOnly}</span>.
            </>
          }
          items={data.faqs}
        />
        <CTAV2 />
      </main>
      <FooterV2 />
    </div>
  );
}
